import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import { toCanberraDateKey } from '../../utils/dateFormatter';

// Status colors matching the RFQ status badge colors
const STATUS_COLORS = {
  'invited': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', dot: 'bg-blue-500' },
  'in_progress': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', dot: 'bg-indigo-500' },
  'submitted': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', dot: 'bg-purple-500' },
  'pending_outcome': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', dot: 'bg-orange-500' },
  'successful': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', dot: 'bg-green-500' },
  'unsuccessful': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', dot: 'bg-red-500' },
};

const CalendarView = ({ rfqs = [] }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('monthly'); // 'weekly' or 'monthly'

  // Filter out unwanted statuses
  const filteredRFQs = useMemo(() => {
    const excludedStatuses = ['not_invited', 'inclusion_requested', 'request_declined', 'not_interested'];
    return rfqs.filter(rfq => !excludedStatuses.includes(rfq.status));
  }, [rfqs]);

  // Group RFQs by date (using close_date)
  const rfqsByDate = useMemo(() => {
    const grouped = {};
    filteredRFQs.forEach(rfq => {
      if (rfq.client_close_datetime) {
        const dateKey = toCanberraDateKey(rfq.client_close_datetime); // YYYY-MM-DD in Canberra time
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(rfq);
      }
    });
    return grouped;
  }, [filteredRFQs]);

  // Get days in month/week
  const getDaysToDisplay = useMemo(() => {
    if (viewMode === 'weekly') {
      // Get current week (Sunday to Saturday)
      const curr = new Date(currentDate);
      const first = curr.getDate() - curr.getDay(); // First day is the day of the month - the day of the week
      const days = [];

      for (let i = 0; i < 7; i++) {
        const day = new Date(curr.setDate(first + i));
        days.push(day);
      }
      return days;
    } else {
      // Get all days in current month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      // Start from the Sunday before the first day of the month
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - startDate.getDay());

      // End on the Saturday after the last day of the month
      const endDate = new Date(lastDay);
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

      const days = [];
      const currentDay = new Date(startDate);

      while (currentDay <= endDate) {
        days.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }

      return days;
    }
  }, [currentDate, viewMode]);

  // Navigation functions
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'weekly') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'weekly') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format header text
  const getHeaderText = () => {
    const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

    if (viewMode === 'weekly') {
      const firstDay = getDaysToDisplay[0];
      const lastDay = getDaysToDisplay[6];

      if (firstDay.getMonth() === lastDay.getMonth()) {
        return `${monthYear} - Week of ${firstDay.getDate()}`;
      } else {
        return `${firstDay.toLocaleString('default', { month: 'short', day: 'numeric' })} - ${lastDay.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
    }

    return monthYear;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in current month (for monthly view)
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Get RFQs for a specific date
  const getRFQsForDate = (date) => {
    // Calendar cells are local Date objects — use local formatting so the key matches
    // rfqsByDate keys (which are the stored UTC date, same as local Canberra date for this app)
    const dateKey = date.toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone
    return rfqsByDate[dateKey] || [];
  };

  // Format status label
  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-4">
      {/* Header with navigation and view toggle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={goToPrevious}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={goToNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={goToToday} className="text-sm">
            Today
          </Button>
          <h2 className="text-lg font-semibold">{getHeaderText()}</h2>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'weekly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('weekly')}
          >
            Weekly
          </Button>
          <Button
            variant={viewMode === 'monthly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('monthly')}
          >
            Monthly
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card className="w-full max-w-full overflow-hidden">
        <div className="p-4">
          {/* Day headers */}
          <div className={`grid ${viewMode === 'weekly' ? 'grid-cols-7' : 'grid-cols-7'} gap-2 mb-2`}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-sm text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className={`grid ${viewMode === 'weekly' ? 'grid-cols-7' : 'grid-cols-7'} gap-2`}>
            {getDaysToDisplay.map((date, index) => {
              const dayRFQs = getRFQsForDate(date);
              const isCurrentDay = isToday(date);
              const inCurrentMonth = isCurrentMonth(date);

              return (
                <div
                  key={index}
                  className={`
                    min-h-[${viewMode === 'weekly' ? '140' : '100'}px]
                    border rounded-lg p-2
                    ${isCurrentDay ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}
                    ${viewMode === 'monthly' && !inCurrentMonth ? 'opacity-40' : ''}
                  `}
                >
                  {/* Date number */}
                  <div className={`text-sm font-medium mb-2 ${isCurrentDay ? 'text-blue-600' : 'text-gray-700'}`}>
                    {date.getDate()}
                  </div>

                  {/* RFQs for this date */}
                  <div className="space-y-1">
                    {dayRFQs.length > 0 && (
                      viewMode === 'weekly' ? (
                        // Weekly view: Show detailed info
                        dayRFQs.map(rfq => {
                          const statusColor = STATUS_COLORS[rfq.status] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
                          return (
                            <div
                              key={rfq.id}
                              onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                              className={`
                                p-2 rounded border cursor-pointer hover:shadow-md transition-shadow
                                ${statusColor.bg} ${statusColor.border}
                              `}
                            >
                              <div className={`text-xs font-medium truncate ${statusColor.text}`}>
                                {rfq.ref_code}
                              </div>
                              <div className="text-[10px] text-gray-600 truncate mt-0.5">
                                {rfq.title}
                              </div>
                              <div className="text-[10px] text-gray-500 truncate">
                                {rfq.clients?.name || 'No client'}
                              </div>
                              <Badge variant="outline" className={`text-[9px] mt-1 ${statusColor.text}`}>
                                {formatStatus(rfq.status)}
                              </Badge>
                            </div>
                          );
                        })
                      ) : (
                        // Monthly view: Show dots
                        <div className="flex flex-wrap gap-1">
                          {dayRFQs.slice(0, 3).map(rfq => {
                            const statusColor = STATUS_COLORS[rfq.status] || { dot: 'bg-gray-400' };
                            return (
                              <div
                                key={rfq.id}
                                onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                                className={`w-2 h-2 rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 ${statusColor.dot}`}
                                title={`${rfq.ref_code} - ${rfq.title} (${formatStatus(rfq.status)})`}
                              />
                            );
                          })}
                          {dayRFQs.length > 3 && (
                            <div className="text-[9px] text-gray-500 ml-1">
                              +{dayRFQs.length - 3}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Legend */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-sm font-medium text-gray-700">Status Colors:</span>
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <div key={status} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${colors.dot}`}></div>
              <span className="text-xs text-gray-600">{formatStatus(status)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default CalendarView;
